import { useState, useMemo } from 'react'
import { MoreVertical, Pencil, Plus, Trash2, Loader2 } from 'lucide-react'
import { usePacks, useFacilities, useDeletePack } from '@/hooks/useBanking'
import { AddPackDialog } from '@/components/banking/AddPackDialog'
import { EditPackDialog } from '@/components/banking/EditPackDialog'
import { ConfirmationDialog } from '@/components/admin/ConfirmationDialog'
import type { Pack } from '@/types/banking'
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Pagination,
  PageHeader,
  toast,
} from '@atrvd/saas-ui'

/** Number of rows shown per page in the Packs table. */
const ITEMS_PER_PAGE = 10

/** Max facility names shown inline in the table before collapsing to "+N more". */
const MAX_VISIBLE_MEMBERS = 3

/**
 * Render the member-facilities cell: the first {@link MAX_VISIBLE_MEMBERS} names
 * joined, then a "+N more" suffix so a pack with many facilities (e.g. 30) can't
 * blow out the table layout. Returns '-' when the pack has no members.
 */
function formatMemberNames(names: string[] | undefined): string {
  if (!names || names.length === 0) return '-'
  if (names.length <= MAX_VISIBLE_MEMBERS) return names.join(', ')
  return `${names.slice(0, MAX_VISIBLE_MEMBERS).join(', ')} +${names.length - MAX_VISIBLE_MEMBERS} more`
}

/**
 * Settings page showing the list of Packs in a table with Add / Edit / Delete
 * row actions (DCP-171).
 *
 * Mirrors SettingsFacilitiesPage in structure: PageHeader + bordered Table +
 * Pagination + action dialogs rendered at the bottom of the component tree.
 *
 * The "Facilities" column is derived client-side by filtering `useFacilities()`
 * for rows whose `pack_id` matches the pack — no extra API call required.
 */
export function SettingsPacksPage() {
  const [currentPage, setCurrentPage] = useState(1)

  // Add dialog state — open flag owned by the page; dialog owns its form.
  const [addDialogOpen, setAddDialogOpen] = useState(false)

  // Edit dialog state — open flag + currently-edited pack. `editingPack` is
  // cleared on close so the form doesn't flash stale data on the next open.
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingPack, setEditingPack] = useState<Pack | null>(null)

  // Delete confirmation state — `deletingPack` drives both the open flag and
  // the confirmation message. Cleared after confirm or cancel.
  const [deletingPack, setDeletingPack] = useState<Pack | null>(null)

  const { data: packs = [], isLoading, error } = usePacks()
  // Full list (incl. inactive): the Facilities column below DISPLAYS pack
  // membership, it does not offer a choice (PR #183 review #2b). An active-only
  // list would silently drop deactivated members here while EditPackDialog —
  // changed by this same ticket to `{ activeOnly: false }` for exactly that
  // reason, with a regression test — still lists them, so the two screens would
  // disagree about the same pack.
  const { data: facilities = [] } = useFacilities({ activeOnly: false })
  const deletePack = useDeletePack()

  /**
   * Map of pack id -> member facility names, built once per `facilities` change
   * rather than on every render. Avoids the O(packs × facilities) filtering that
   * a per-row helper would do, giving an O(1) lookup in the table. Names are kept
   * as an array so the cell can truncate to "+N more" (see formatMemberNames).
   * Facilities with no pack are skipped.
   */
  const memberNamesByPackId = useMemo(() => {
    const map = new Map<string, string[]>()
    for (const f of facilities) {
      if (!f.pack_id) continue
      const existing = map.get(f.pack_id)
      if (existing) existing.push(f.name)
      else map.set(f.pack_id, [f.name])
    }
    return map
  }, [facilities])

  /**
   * Confirm deletion of the selected pack. Closes the dialog only on success;
   * on failure it stays open (with an error toast) so the user can retry without
   * re-opening the menu — consistent with the Add/Edit dialogs' error handling.
   * (useDeletePack has no onError, so the error must be surfaced here.)
   */
  const handleDeleteConfirm = async () => {
    if (!deletingPack) return
    try {
      await deletePack.mutateAsync(deletingPack.id)
      toast.success('Pack deleted')
      setDeletingPack(null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to delete pack')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive">Failed to load packs</p>
      </div>
    )
  }

  const totalPages = Math.ceil(packs.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const paginatedPacks = packs.slice(startIndex, startIndex + ITEMS_PER_PAGE)

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Packs (${packs.length})`}
        description="Manage facility packs"
        actions={
          <Button className="bg-red-700 hover:bg-red-800" onClick={() => setAddDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Pack
          </Button>
        }
      />

      {/* Packs Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="font-semibold">Pack Name</TableHead>
              <TableHead className="font-semibold">Facilities</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedPacks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                  No packs found
                </TableCell>
              </TableRow>
            ) : (
              paginatedPacks.map((pack) => (
                <TableRow key={pack.id} className="hover:bg-muted/30">
                  <TableCell className="font-medium">{pack.name}</TableCell>
                  {/* `title` carries the full member list so it stays accessible
                      on hover even when the cell collapses to "+N more". */}
                  <TableCell
                    className="text-muted-foreground"
                    title={memberNamesByPackId.get(pack.id)?.join(', ')}
                  >
                    {formatMemberNames(memberNamesByPackId.get(pack.id))}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            setEditingPack(pack)
                            setEditDialogOpen(true)
                          }}
                        >
                          <Pencil className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setDeletingPack(pack)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination — only rendered when there is more than one page */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            maxPageButtons={5}
          />
        </div>
      )}

      {/* Add Pack Dialog — owned add form, reset on open */}
      <AddPackDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} />

      {/* Edit Pack Dialog — seeded from editingPack, cleared on close */}
      <EditPackDialog
        open={editDialogOpen}
        onOpenChange={(o) => {
          setEditDialogOpen(o)
          // Clear the selected pack on close so the next open starts fresh.
          if (!o) setEditingPack(null)
        }}
        pack={editingPack}
      />

      {/* Delete Confirmation Dialog — mirrors SettingsCategoriesPage pattern */}
      <ConfirmationDialog
        open={!!deletingPack}
        onOpenChange={(open: boolean) => !open && !deletePack.isPending && setDeletingPack(null)}
        title="Delete Pack"
        description={`Are you sure you want to delete "${deletingPack?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleDeleteConfirm}
        isLoading={deletePack.isPending}
        variant="destructive"
      />
    </div>
  )
}
